import { useCallback, useMemo } from "react";
import { View } from "ui/atoms/view";
import { Text } from "react-native";
import FrontBodyDiagramComponent from "ui/gluestack/body-map/FrontBodyDiagramSvg";
import BackBodyDiagramComponent from "ui/gluestack/body-map/BackBodyDiagramSvg";
import { BODY_PART_LABELS } from "../../../constants/bodyMapConstants";

interface Props {
  selectedParts: string[];
  onChange?: (parts: string[]) => void;
}

export function BodyMap({ selectedParts, onChange }: Props) {
  const isSelected = useCallback(
    (part: string) => selectedParts.includes(part),
    [selectedParts],
  );
  const toggleSelection = useCallback(
    (part: string) => {
      if (!onChange) return;
      const newParts = selectedParts.includes(part)
        ? selectedParts.filter((p) => p !== part)
        : [...selectedParts, part];
      onChange(newParts);
    },
    [selectedParts, onChange],
  );

  const frontBodyParts = useMemo(
    () => selectedParts.filter((part) => part.endsWith("_front")),
    [selectedParts],
  );
  const backBodyParts = useMemo(
    () => selectedParts.filter((part) => part.endsWith("_back")),
    [selectedParts],
  );

  return (
    <View>
      <Text className="text-base font-normal text-typography-500">
        Choose all body parts affected by one issue and add details. Start a new
        entry for each different issue.
      </Text>
      <View className="my-16 flex-row place-content-center gap-10 sm:gap-16 lg:gap-28">
        <FrontBodyDiagramComponent
          toggleSelection={toggleSelection}
          isSelected={isSelected}
        />
        <BackBodyDiagramComponent
          toggleSelection={toggleSelection}
          isSelected={isSelected}
        />
      </View>
      <Text className="mb-5 text-lg font-medium text-typography-800">
        Selected Body Parts and Relevant Details
      </Text>
      <Text className="mb-4 text-lg font-medium text-primary-700">Front</Text>
      <View className="mb-4 flex-row gap-4 flex-wrap">
        {frontBodyParts.length > 0 ? (
          frontBodyParts.map((part, index) => (
            <Text key={part}>
              {index + 1}. {BODY_PART_LABELS[part] || part}
            </Text>
          ))
        ) : (
          <Text>No body parts selected</Text>
        )}
      </View>
      <Text className="mb-4 text-lg font-medium text-primary-700">Back</Text>
      <View className="mb-10 flex-row gap-4 flex-wrap">
        {backBodyParts.length > 0 ? (
          backBodyParts.map((part, index) => (
            <Text key={part}>
              {index + 1}. {BODY_PART_LABELS[part] || part}
            </Text>
          ))
        ) : (
          <Text>No body parts selected</Text>
        )}
      </View>
    </View>
  );
}
